/**
 * Free Results — Priority Observations card.
 *
 * Replaces the older "Top 3 Observations" / "Top 3 Weaknesses" cards on the
 * free result page. Ranking logic is delegated to `getPriorityObservations`
 * which returns one of three shapes (`all_strong`, `clustered`, `ranked`) so
 * the UI never labels a 4.5/5.0 domain as a "weakness" by accident.
 */
import { Target } from '@phosphor-icons/react';

const COLOURS = {
  navy: '#0B2951',
  teal: '#008C85',
  gray: '#6D7586',
};

const Preamble = ({ kind, minScore }) => {
  if (kind === 'all_strong') {
    return (
      <p className="text-sm leading-relaxed" style={{ color: COLOURS.gray }} data-testid="priority-preamble-strong">
        All assessed domains score {minScore.toFixed(2)} or above. The organisation does not present a single
        dominant weakness in this snapshot. The areas listed below are simply the lowest-scoring on a
        comparative basis — they are not flagged as risks.
      </p>
    );
  }
  if (kind === 'clustered') {
    return (
      <p className="text-sm leading-relaxed" style={{ color: COLOURS.gray }} data-testid="priority-preamble-clustered">
        The domain scores are clustered at a similar maturity level. These domains are among the areas to
        review first, but no single area stands out as significantly weaker than the others.
      </p>
    );
  }
  return (
    <p className="text-sm leading-relaxed" style={{ color: COLOURS.gray }} data-testid="priority-preamble-ranked">
      The lowest-scoring domains in this assessment. The full domain-by-domain consulting analysis is
      available in the Professional Diagnostic Report.
    </p>
  );
};

const ObservationRow = ({ item, index, neutral }) => {
  const score = Number(item.score);
  const accent = neutral ? COLOURS.gray : COLOURS.teal;
  return (
    <li
      className="flex gap-4 py-4 border-t border-slate-100 first:border-t-0"
      data-testid={`priority-observation-${index}`}
    >
      <div
        className="flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
        style={{ background: `${accent}1A`, color: accent }}
      >
        {index + 1}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-3 mb-1">
          <div className="font-semibold text-sm sm:text-base" style={{ color: COLOURS.navy }}>
            {item.domain}
          </div>
          <div className="text-sm font-bold" style={{ color: accent }}>
            {score.toFixed(1)} / 5.0
          </div>
        </div>
        <p className="text-sm text-slate-700 mb-1.5 leading-relaxed">
          <span className="font-medium" style={{ color: COLOURS.navy }}>Why it matters: </span>
          {item.whyItMatters}
        </p>
        <p className="text-sm text-slate-600 leading-relaxed">
          <span className="font-medium" style={{ color: COLOURS.navy }}>Suggested first action: </span>
          {item.firstAction}
        </p>
      </div>
    </li>
  );
};

const PriorityObservations = ({ observations }) => (
  <section
    className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm mb-8"
    data-testid="priority-observations-card"
  >
    <div className="flex items-center gap-3 mb-2">
      <div
        className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ background: `${COLOURS.teal}1A` }}
      >
        <Target size={20} weight="bold" style={{ color: COLOURS.teal }} />
      </div>
      <h3 className="text-lg sm:text-xl font-bold" style={{ color: COLOURS.navy }}>
        Priority Observations
      </h3>
    </div>
    <p className="text-sm mb-4 leading-relaxed" style={{ color: COLOURS.gray }}>
      A brief preview of the areas that may require management attention. Full analysis is available
      in the Professional Diagnostic Report.
    </p>
    <Preamble kind={observations.kind} minScore={observations.minScore} />
    <ul className="mt-4">
      {observations.items.map((item, idx) => (
        <ObservationRow
          key={item.domain}
          item={item}
          index={idx}
          neutral={observations.kind !== 'ranked'}
        />
      ))}
    </ul>
  </section>
);

export default PriorityObservations;
